import React, { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { CONTACT_INFO } from "../i18n/translations";
import { toast } from "sonner";

const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", company: "", email: "", interest: "", message: "" });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(lang === "es" ? "Por favor completa los campos requeridos." : "Please fill the required fields.");
      return;
    }
    const subject = lang === "es"
      ? `Nuevo contacto desde ari-iva.com — ${form.name}`
      : `New contact from ari-iva.com — ${form.name}`;
    const lines = [
      lang === "es" ? "Hola ARIIVA, me gustaría iniciar una conversación." : "Hello ARIIVA, I would like to start a conversation.",
      "",
      `${t.contact.form.name}: ${form.name}`,
      form.company ? `${t.contact.form.company}: ${form.company}` : null,
      `${t.contact.form.email}: ${form.email}`,
      form.interest ? `${t.contact.form.interest}: ${form.interest}` : null,
      "",
      `${t.contact.form.message}:`,
      form.message,
    ].filter(Boolean);
    window.location.href = `mailto:${CONTACT_INFO.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join("\n"))}`;
    toast.success(lang === "es" ? "Abriendo tu cliente de correo..." : "Opening your mail client...");
  };

  const divisions = ["ARIIVA Infra","ARIIVA Assets","ARIIVA Real Estate","ARIIVA Projects","ARIIVA Digital","ARIIVA Human","ARIIVA Advisory"];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#001a45] text-white py-24 md:py-36 overflow-hidden grain-overlay border-t border-white/10"
    >
      <div className="absolute inset-0 pointer-events-none opacity-40" style={{ background: "radial-gradient(700px 500px at 85% 20%, rgba(111,45,189,0.5), transparent 60%)" }} />

      <div className="relative max-w-[1480px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-6"
          >
            <p className="text-[11px] tracking-[0.35em] uppercase text-[#B89DDE] font-semibold mb-6 flex items-center gap-3">
              <span className="inline-block w-10 h-px bg-[#B89DDE]" />
              {t.contact.overline}
            </p>
            <h2 data-testid="contact-title" className="font-display text-4xl md:text-5xl lg:text-6xl uppercase leading-[1.02]">
              {t.contact.title}
            </h2>
            <p className="mt-8 text-base md:text-lg text-white/75 max-w-lg font-editorial font-light leading-relaxed">
              {t.contact.description}
            </p>

            <div className="mt-14 space-y-8">
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2 font-semibold">{t.contact.channels.emailLabel}</p>
                <a href={`mailto:${CONTACT_INFO.email}`} data-testid="contact-email-link" className="font-display text-2xl md:text-3xl break-all hover:text-[#B89DDE] transition-colors">
                  {CONTACT_INFO.email}
                </a>
              </div>
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2 font-semibold">{t.contact.channels.addressLabel}</p>
                <p className="font-display text-xl md:text-2xl text-white/85">{t.contact.channels.address}</p>
              </div>
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2 font-semibold">LinkedIn</p>
                <a href={CONTACT_INFO.linkedin} target="_blank" rel="noreferrer" data-testid="contact-linkedin-link" className="font-display text-xl md:text-2xl hover:text-[#B89DDE] transition-colors">
                  ARIIVA en LinkedIn ↗
                </a>
              </div>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="lg:col-span-6 bg-white/[0.04] border border-white/10 p-8 md:p-12 backdrop-blur-md"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div className="md:col-span-2">
                <label className="text-[10px] tracking-[0.25em] uppercase text-white/55 block mb-2 font-semibold">{t.contact.form.name} *</label>
                <input type="text" name="name" value={form.name} onChange={handleChange} required data-testid="contact-input-name" className="ariiva-input text-white" placeholder="—" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-white/55 block mb-2 font-semibold">{t.contact.form.company}</label>
                <input type="text" name="company" value={form.company} onChange={handleChange} data-testid="contact-input-company" className="ariiva-input text-white" placeholder="—" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-white/55 block mb-2 font-semibold">{t.contact.form.email} *</label>
                <input type="email" name="email" value={form.email} onChange={handleChange} required data-testid="contact-input-email" className="ariiva-input text-white" placeholder="—" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] tracking-[0.25em] uppercase text-white/55 block mb-3 font-semibold">{t.contact.form.interest}</label>
                <div className="flex flex-wrap gap-2">
                  {divisions.map((d) => (
                    <button
                      type="button"
                      key={d}
                      onClick={() => setForm({ ...form, interest: d })}
                      data-testid={`contact-interest-${d.toLowerCase().replace(/\s/g, "-")}`}
                      className={`text-[10px] tracking-[0.2em] uppercase font-semibold px-3.5 py-2 border transition-colors ${
                        form.interest === d
                          ? "bg-white text-[#002B6B] border-white"
                          : "border-white/25 text-white/75 hover:border-white/60 hover:text-white"
                      }`}
                    >{d}</button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] tracking-[0.25em] uppercase text-white/55 block mb-2 font-semibold">{t.contact.form.message} *</label>
                <textarea name="message" value={form.message} onChange={handleChange} rows={4} required data-testid="contact-input-message" className="ariiva-input text-white resize-none" placeholder="—" />
              </div>
            </div>

            <div className="mt-10 flex flex-col-reverse md:flex-row items-start md:items-center justify-between gap-6">
              <p className="text-[11px] text-white/45 max-w-xs leading-relaxed">{t.contact.form.privacy}</p>
              <button
                type="submit"
                data-testid="email-submit-button"
                className="group inline-flex items-center gap-3 bg-white text-[#002B6B] px-8 py-4 text-[12px] tracking-[0.22em] uppercase font-bold hover:bg-[#B89DDE] hover:text-white transition-colors"
              >
                {t.contact.form.submit}
                <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
